import { CSSProperties, useMemo } from 'react';
import { observer } from 'mobx-react';

import Loader, { WHITE, X_SMALL } from '@/common/components/Loader/Loader';
import DriveItemPreview from '@/drive/components/DriveItemPreview/DriveItemPreview';
import { PinnedItemsPreviewData } from '@/forwarding/types/ForwardPreviewData';

import PinnedItemPreview from './PinnedItemPreview/PinnedItemPreview';
import styles from './PinnedItemsStackPreview.module.css';

export interface PinnedItemsStackPreviewProps {
  preview: PinnedItemsPreviewData;
  size: number;
}

const MAX_CARDS = 3;

// Fan layout for the macOS-like stack: front card is upright and on top, the rest peek behind it.
const CARD_LAYOUT = [
  { rotate: 0, zIndex: 3 },
  { rotate: 15, zIndex: 2 },
  { rotate: -15, zIndex: 1 },
];

function PinnedItemsStackPreview(props: PinnedItemsStackPreviewProps) {
  const { preview, size } = props;
  const { items, dtoItems } = preview;

  const isLoading = !dtoItems;

  // newest items shown first
  const visibleItems = useMemo(() => (dtoItems ?? []).slice(0, MAX_CARDS), [dtoItems]);

  if (items.length === 1) {
    return (
      <PinnedItemPreview
        preview={visibleItems[0] ?? null}
        size={size}
        isLoading={isLoading}
        className={styles.stack}
      />
    );
  }

  const cardSize = Math.round(size * 0.7);
  const cardsCount = isLoading ? Math.min(items.length, MAX_CARDS) : visibleItems.length;

  return (
    <div className={styles.stack} style={{ width: size, height: size }}>
      {Array.from({ length: cardsCount }).map((_, index) => {
        const layout = CARD_LAYOUT[index];

        return (
          <div
            key={index}
            className={styles.card}
            style={
              {
                width: cardSize,
                height: cardSize,
                zIndex: layout.zIndex,
                '--rotate': `${layout.rotate}deg`,
              } as CSSProperties
            }
          >
            {visibleItems[index] && <DriveItemPreview preview={visibleItems[index]} size={cardSize} />}
          </div>
        );
      })}

      {isLoading && <Loader className={styles.loader} appearance={WHITE} size={X_SMALL} />}
    </div>
  );
}

export default observer(PinnedItemsStackPreview);
